import { useState } from "react";
import ActionsPanel from "./ActionsPanel";
import AttributesPanel from "./AttributesPanel";
import { matchesQuery, SearchField, SelectField } from "./fields";
import HoursEditor from "./HoursEditor";
import OpenStatus from "./OpenStatus";
import SpecialHours from "./SpecialHours";
import type { Place, PlaceAction, PlaceAttr, PlaceGroup, PlaceHoursPeriod, SpecialHour } from "../types";

type Section = "hours" | "open" | "special" | "attrs" | "actions";

type Props = {
  places: Place[];
  groups: PlaceGroup[];
  selected: string;
  draft: PlaceHoursPeriod[];
  busy: boolean;
  onSelect: (name: string) => void;
  onDraft: (next: PlaceHoursPeriod[]) => void;
  onSave: () => void;
  onAssign: (location: string, groupId: string) => void;
  special: SpecialHour[];
  onSpecial: (rows: SpecialHour[]) => void;
  onSaveSpecial: () => void;
  specialBusy: boolean;
  onOpen: (status: string) => void;
  openBusy: boolean;
  attrs: PlaceAttr[];
  onToggleAttr: (id: string) => void;
  onSaveAttrs: () => void;
  attrsBusy: boolean;
  actions: PlaceAction[];
  actionTypes: Record<string, string>;
  onAddAction: (type: string, uri: string) => Promise<void>;
  onDeleteAction: (name: string) => void;
  actionsBusy: boolean;
};

const SECTIONS: { id: Section; label: string }[] = [
  { id: "hours", label: "Horaires" },
  { id: "open", label: "Ouverture" },
  { id: "special", label: "Exceptions" },
  { id: "attrs", label: "Attributs" },
  { id: "actions", label: "Actions" },
];

export default function PlacesPanel(props: Props) {
  const [section, setSection] = useState<Section>("hours");
  const [query, setQuery] = useState("");
  const place = props.places.find((p) => p.name === props.selected) || props.places[0];
  const listed = props.places.filter((p) =>
    matchesQuery(`${p.title} ${p.address} ${p.phone} ${p.group_name || ""}`, query),
  );

  if (!place) {
    return (
      <section className="pr-card px-6 py-16 text-center text-sm text-slate-500">
        Aucune fiche dans ce groupe.
      </section>
    );
  }

  return (
    <section className="grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
      <aside className="pr-card p-3">
        <p className="px-3 pb-2 pt-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
          Établissements
        </p>
        <div className="px-1 pb-2">
          <SearchField value={query} onChange={setQuery} placeholder="Nom, adresse, téléphone" />
        </div>
        <ul className="max-h-[68vh] space-y-1 overflow-auto">
          {listed.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-slate-400">Aucune fiche pour cette recherche.</li>
          ) : null}
          {listed.map((p) => (
            <li key={p.name}>
              <button
                type="button"
                onClick={() => props.onSelect(p.name)}
                className={`w-full rounded-2xl px-3 py-3 text-left ${
                  p.name === place.name ? "bg-violet text-white" : "hover:bg-mist"
                }`}
              >
                <span className="block truncate text-sm font-medium">{p.title}</span>
                <span className={`mt-0.5 block truncate text-xs ${p.name === place.name ? "text-white/70" : "text-slate-500"}`}>
                  {p.address}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <div key={place.name} className="pr-fade pr-card p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">{place.title}</h2>
            <p className="mt-1 text-sm text-slate-500">{place.address || "Adresse non renseignée"}</p>
            {place.phone ? <p className="text-sm text-slate-500">{place.phone}</p> : null}
          </div>
          <div className="min-w-[220px]">
            <p className="mb-1 text-xs text-slate-500">Groupe</p>
            <SelectField
              value={place.group_id || ""}
              onChange={(id) => props.onAssign(place.name, id)}
              options={[
                { value: "", label: "Sans groupe" },
                ...props.groups.map((g) => ({
                  value: g.id,
                  label: g.source === "google" ? `${g.name} (Google)` : g.name,
                })),
              ]}
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSection(s.id)}
              className={`pr-chip ${section === s.id ? "is-on" : "is-off"}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {section === "hours" ? (
            <div>
              <div className="mb-5 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold">Horaires habituels</h3>
                  <p className="mt-1 text-sm text-slate-500">Un jour, une plage. Rien de plus.</p>
                </div>
                <button type="button" disabled={props.busy} onClick={props.onSave} className="pr-btn">
                  {props.busy ? "Enregistrement…" : "Enregistrer"}
                </button>
              </div>
              <HoursEditor hours={props.draft} onChange={props.onDraft} />
            </div>
          ) : null}
          {section === "open" ? (
            <OpenStatus status={place.open_status || "OPEN"} busy={props.openBusy} onSave={props.onOpen} />
          ) : null}
          {section === "special" ? (
            <SpecialHours rows={props.special} busy={props.specialBusy} onChange={props.onSpecial} onSave={props.onSaveSpecial} />
          ) : null}
          {section === "attrs" ? (
            <AttributesPanel attrs={props.attrs} busy={props.attrsBusy} onToggle={props.onToggleAttr} onSave={props.onSaveAttrs} />
          ) : null}
          {section === "actions" ? (
            <ActionsPanel
              actions={props.actions}
              types={props.actionTypes}
              busy={props.actionsBusy}
              onAdd={props.onAddAction}
              onDelete={props.onDeleteAction}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
}
